import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';

const getSubcategoriesForCategory = gql`
  query CategorySubcategories($categoryId: String!) {
    navigationCategory(categoryId: $categoryId) {
      categoryId
      hideChildren
      name
      url
      dropdownTopValue
      navigationSubCategories {
        categoryId
        displayType
        hideCategory
        name
        url
      }
    }
  }
`;

/**
 * Custom React hook to fetch and process subcategories for a given parent category,
 * and generate either dropdown options or tabs for category navigation
 *
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.parentCategoryId - The ID of the parent category (required).
 * @param {string} [params.currentCategoryId] - The ID of the currently selected category.
 * @param {"dropdown"|"tabs"} [params.mode="dropdown"] - The format of returned data.
 *
 * @returns {Object} based on mode:
 *   If dropdown:
 *     - categoryOptions: Array<{label: string, value: string}>
 *     - defaultCategory: {value: string, index: number}|undefined
 *   If tabs:
 *     - tabValues: Array<{id: string, label: string, url: string}>
 *     - defaultTab: {id: string, isSelected: boolean}
 *   Default returns:
 *     - handleChange: function to handle navigation
 */
const useFetchNavCategories = ({
  parentCategoryId,
  currentCategoryId = '',
  mode = 'dropdown',
}) => {
  const { data } = useQuery(getSubcategoriesForCategory, {
    variables: {
      categoryId: parentCategoryId,
    },
  });

  const categories = useMemo(() => {
    const regularSubcategories = [];
    const clearanceSubcategories = [];
    const featuredSubcategories = [];

    let defaultCategory;
    let defaultTab;

    if (!data?.navigationCategory?.hideChildren) {
      data?.navigationCategory?.navigationSubCategories.forEach(({
        categoryId, displayType, hideCategory, name, url,
      }, index) => {
        if (!hideCategory) {
          const dropdownObj = { label: name, value: url };
          const tabObj = { id: categoryId, label: name, url };

          switch (displayType) {
            case 'regular':
              // eslint-disable-next-line no-unused-expressions
              mode === 'dropdown'
                ? regularSubcategories.push(dropdownObj)
                : regularSubcategories.push(tabObj);
              break;
            case 'clearance':
              // eslint-disable-next-line no-unused-expressions
              mode === 'dropdown'
                ? clearanceSubcategories.push(dropdownObj)
                : clearanceSubcategories.push(tabObj);
              break;
            case 'featured':
              // eslint-disable-next-line no-unused-expressions
              mode === 'dropdown'
                ? featuredSubcategories.push(dropdownObj)
                : featuredSubcategories.push(tabObj);
              break;
            default:
              break;
          }

          if (categoryId === currentCategoryId) {
            if (mode === 'dropdown') {
              defaultCategory = { value: url, index };
            } else {
              defaultTab = { id: categoryId, isSelected: true };
            }
          }
        }
      });
    }

    /* mode dropdown */
    if (mode === 'dropdown') {
      const categoryOptions = [
        ...(data?.navigationCategory?.dropdownTopValue
          ? [{ label: `${data.navigationCategory.dropdownTopValue}`, value: data?.navigationCategory?.url }]
          : []),
        ...regularSubcategories,
        ...clearanceSubcategories,
        ...featuredSubcategories,
      ];

      const handleChange = (e) => {
        window.location.href = e.target.value;
      };

      return {
        categoryOptions,
        defaultCategory,
        handleChange,
      };
    }

    /* mode tabs */
    let tabDefaultTab = defaultTab;
    if (!tabDefaultTab) {
      tabDefaultTab = { id: data?.navigationCategory?.categoryId, isSelected: true };
    }

    const tabValues = [
      ...(data?.navigationCategory?.dropdownTopValue
        ? [{ id: data?.navigationCategory?.categoryId, label: `${data.navigationCategory.dropdownTopValue}`, url: data?.navigationCategory?.url }]
        : []),
      ...regularSubcategories,
      ...clearanceSubcategories,
      ...featuredSubcategories,
    ];

    const handleChange = (tab) => {
      window.location.href = tab?.url;
    };

    return {
      tabValues,
      defaultTab: tabDefaultTab,
      handleChange,
    };
  }, [data, mode, currentCategoryId]);

  return categories;
};

export default useFetchNavCategories;
